import React from "react";
import UploadInputField from "component/auth/onboarding/UploadInputField";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import AddDirectors from "../AddDirectors";
import ControlPerson from "../ControlPerson";
import { businessTypeOptions, natureOfBusinessOptions } from "../BusinessDetails";
import {
  CURRENCY_OPTIONS,
  SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE,
  SOURCE_OF_FUNDS_OPTIONS,
  emptyFileState,
} from "./constants";
import {
  formatAmount,
  getDateMax,
  normalizeKey,
} from "./normalizationUtils";
import PhoneCountryDropdown from "./PhoneCountryDropdown";

export const RequirementFieldRenderer = ({
  requirement,
  value,
  files,
  prefillData,
  countryOptions,
  kybCountriesLoading,
  phoneCountryCode,
  phoneCountryOptions,
  countriesLoading,
  country,
  phoneError,
  getPhoneDialCode,
  setPhoneCountryTouched,
  setPhoneCountryCode,
  setPhoneError,
  updateValue,
  updateAddress,
  handleFileChange,
  removeFile,
  handleUpdateMoreData,
}) => {
  const renderUploadField = (req, fileId = req.id, label = req.label, disabled = false) => (
    <UploadInputField
      key={fileId}
      label={label}
      subtitle="JPEG, PNG, JPG, PDF ・ 5 MB max."
      fileKey={`update-${fileId}`}
      fileState={files[fileId] || emptyFileState}
      onFileChange={(event) => handleFileChange(fileId, event)}
      onRemove={() => removeFile(fileId)}
      disabled={disabled}
    />
  );

  // Director and UBO requirements are already deduplicated in the hook
  // (renderableRequirements). The _formType field signals which form to render.
  if (requirement._formType === "director") {
    return (
      <AddDirectors
        isUpdating={true}
        handleUpdateMoreData={handleUpdateMoreData}
        prefillData={prefillData}
      />
    );
  }

  if (requirement._formType === "ubo") {
    return (
      <ControlPerson
        isUpdating={true}
        handleUpdateMoreData={handleUpdateMoreData}
        prefillData={prefillData}
      />
    );
  }

  if (requirement.type === "document" || requirement.type === "upload") {
    return renderUploadField(requirement);
  }

  if (requirement.type === "gender" || requirement.key === "gender") {
    return (
      <div key={requirement.id} data-onboarding-field={requirement.key}>
        <p className="aku-body-small-medium mb-2 text-[#4F5372]">{requirement.label}</p>
        <Dropdown
          value={value || ""}
          options={[
            { label: "Male", value: "MALE" },
            { label: "Female", value: "FEMALE" },
          ]}
          placeholder="Select gender"
          onChange={(gender) => updateValue(requirement.id, gender)}
        />
      </div>
    );
  }

  if (requirement.key === "business_type" || requirement.type === "business_type") {
    return (
      <div key={requirement.id} data-onboarding-field={requirement.key}>
        <div data-onboarding-field="businessType">
          <Dropdown
            value={value || ""}
            label="Business type"
            labelClassName="aku-body-small-medium !mb-2"
            placeholder="Select business type"
            options={businessTypeOptions}
            onChange={(val) => updateValue(requirement.id, val)}
          />
        </div>
      </div>
    );
  }

  if (
    requirement.key === "nature_of_business" ||
    requirement.type === "nature_of_business" ||
    normalizeKey(requirement.key) === "nature_of_business" ||
    normalizeKey(requirement.type) === "nature_of_business"
  ) {
    const selectedVal =
      typeof value === "object" && value !== null
        ? value.code || value.nature || value.value
        : value;

    return (
      <div key={requirement.id} data-onboarding-field="natureOfBusiness">
        <Dropdown
          value={selectedVal || ""}
          label={requirement.label || "Nature of business"}
          labelClassName="aku-body-small-medium !mb-2"
          placeholder="Please select nature of business"
          options={natureOfBusinessOptions}
          onChange={(val) => updateValue(requirement.id, val)}
        />
      </div>
    );
  }

  if (requirement.type === "country" || requirement.key.includes("country")) {
    const selectedVal = typeof value === "object" ? value?.name : value;

    return (
      <div key={requirement.id} data-onboarding-field={requirement.key}>
        <p className="aku-body-small-medium mb-2 text-[#4F5372]">{requirement.label}</p>
        <Dropdown
          value={selectedVal || ""}
          options={countryOptions}
          placeholder="Select country"
          searchable
          searchPlaceholder="Search country"
          readOnly={kybCountriesLoading}
          onChange={(countryName) => {
            const selectedOption = countryOptions.find((opt) => opt.value === countryName);
            const rawCountry = selectedOption?.raw || {};
            const countryObj = {
              name: countryName,
              code: rawCountry.code || selectedOption?.code || "",
              continent: rawCountry.continent || selectedOption?.continent || "",
              flag: rawCountry.flag || selectedOption?.flag || "",
            };

            updateValue(requirement.id, countryObj);
          }}
        />
      </div>
    );
  }

  if (requirement.type === "address") {
    return (
      <div key={requirement.id}>
        <p className="aku-body-small-medium mb-2 text-[#4F5372]">{requirement.label}</p>
        <div className="space-y-2">
          <Input
            type="text"
            value={value?.houseNumber || ""}
            onChange={(nextValue) => updateAddress(requirement.id, "houseNumber", nextValue)}
            placeholder="House number"
          />
          <Input
            type="text"
            value={value?.street || ""}
            onChange={(nextValue) => updateAddress(requirement.id, "street", nextValue)}
            placeholder="Street address"
          />
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            <Input
              type="text"
              value={value?.city || ""}
              onChange={(nextValue) => updateAddress(requirement.id, "city", nextValue)}
              placeholder="City"
              className="!mt-0"
            />
            <Input
              type="text"
              value={value?.state || ""}
              onChange={(nextValue) => updateAddress(requirement.id, "state", nextValue)}
              placeholder="State / Province"
              className="!mt-0"
            />
            <Input
              type="text"
              value={value?.zipCode || ""}
              onChange={(nextValue) => updateAddress(requirement.id, "zipCode", nextValue)}
              placeholder="Postal / ZIP Code"
              className="!mt-0"
            />
          </div>
        </div>
      </div>
    );
  }

  if (requirement.type === "web_presence") {
    return (
      <div className="space-y-2" key={requirement.id}>
        <p className="aku-body-normal-medium mb-2 text-[#4F5372]">Business website</p>
        <Input
          type="url"
          value={value?.businessWebsite || ""}
          onChange={(nextValue) =>
            updateValue(requirement.id, {
              ...value,
              businessWebsite: nextValue,
            })
          }
          placeholder="Enter web address"
          className="!mt-0"
        />
      </div>
    );
  }

  if (requirement.type === "source_of_fund" || requirement.type === "source_of_funds") {
    const docOptions = SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[value?.source] || [];
    return (
      <div className="space-y-2" key={requirement.id}>
        <div>
          <p className="aku-body-small-medium mb-2 text-[#4F5372]">{requirement.label}</p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <Dropdown
              value={value?.source || ""}
              options={SOURCE_OF_FUNDS_OPTIONS}
              placeholder="Select source of funds"
              onChange={(source) =>
                updateValue(requirement.id, {
                  source,
                  documentType: "",
                  document: null,
                })
              }
            />
            <Dropdown
              value={value?.documentType || ""}
              options={docOptions}
              placeholder={value?.source ? "Document type" : "Select source first"}
              readOnly={!value?.source}
              onChange={(documentType) =>
                updateValue(requirement.id, {
                  ...value,
                  documentType,
                })
              }
            />
          </div>
        </div>
        {renderUploadField(requirement, `${requirement.id}-source`, "Upload document")}
      </div>
    );
  }

  if (requirement.type === "monthly_transaction") {
    return (
      <div key={requirement.id}>
        <p className="aku-body-small-medium mb-2 text-[#4F5372]">{requirement.label}</p>
        <div className="grid grid-cols-1 items-center gap-2 md:grid-cols-[140px_1fr]">
          <Dropdown
            value={value?.currency || "NGN"}
            options={CURRENCY_OPTIONS}
            onChange={(currency) =>
              updateValue(requirement.id, { ...value, currency })
            }
          />
          <Input
            type="text"
            inputMode="numeric"
            value={value?.amount || ""}
            onChange={(nextValue) =>
              updateValue(requirement.id, {
                ...value,
                amount: formatAmount(nextValue),
              })
            }
            placeholder="e.g. 100,000"
            className="!mt-0"
          />
        </div>
      </div>
    );
  }

  if (requirement.type === "date") {
    return (
      <Input
        key={requirement.id}
        label={requirement.label}
        labelClassName="aku-body-small-medium !mb-2"
        type="date"
        max={getDateMax()}
        value={value || ""}
        onChange={(nextValue) => updateValue(requirement.id, nextValue)}
        placeholder="Select date"
        className="!mt-0"
      />
    );
  }

  if (requirement.type === "select") {
    return (
      <div className="space-y-2" key={requirement.id}>
        <Input
          label={requirement.label}
          labelClassName="aku-body-small-medium !mb-2"
          type="text"
          value={value || ""}
          onChange={(nextValue) => updateValue(requirement.id, nextValue)}
          placeholder="Enter values separated by commas"
          className="!mt-0"
        />
        <p className="text-xs text-[#667085]">Separate multiple values with commas.</p>
      </div>
    );
  }

  if (requirement.type === "phone_number" || requirement.type === "phone") {
    const formData = { phoneNumber: value || "" };
    const handleChange = (_field, val) => updateValue(requirement.id, val);

    return (
      <div key={requirement.id}>
        <label className="aku-body-small-medium block text-[#344054] mb-2">
          {requirement.label || "Company phone number"}
        </label>
        <div className="flex h-[56px] w-full items-center gap-2 rounded-lg">
          <PhoneCountryDropdown
            value={phoneCountryCode}
            options={phoneCountryOptions}
            onChange={(val) => {
              setPhoneCountryTouched(true);
              setPhoneCountryCode(val);
              setPhoneError("");
            }}
            loading={countriesLoading}
          />
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-5 top-1/2 z-10 -translate-y-1/2 text-[#101828]">
              {getPhoneDialCode(phoneCountryCode)}
            </span>
            <Input
              type="tel"
              placeholder={
                country === "Nigeria"
                  ? "905 555 0123"
                  : country === "United States"
                    ? "415 555 2671"
                    : "Enter phone number"
              }
              maxLength={15}
              value={formData.phoneNumber}
              onChange={(e) =>
                handleChange("phoneNumber", e.replace(/[^\d]/g, ""))
              }
              className="!mt-0 !pl-20"
            />
          </div>
        </div>
        {phoneCountryCode === "NG" && formData.phoneNumber && (
          <p className="mt-2 text-sm text-[#6F739B]">
            We'll send an OTP to verify this number
          </p>
        )}
        {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
      </div>
    );
  }

  const inputType = requirement.type === "email" ? "email" : "text";
  return (
    <Input
      key={requirement.id}
      label={requirement.label}
      labelClassName="aku-body-small-medium !mb-2"
      type={inputType}
      value={value || ""}
      onChange={(nextValue) => updateValue(requirement.id, nextValue)}
      placeholder={`Enter ${String(requirement.label).toLowerCase()}`}
      className="!mt-0"
    />
  );
};

export default RequirementFieldRenderer;
